import { useTranslation, type UseTranslationOptions } from 'react-i18next';
import { useCallback } from 'react';
import i18n, { type Namespace, type SupportedLanguage, supportedLanguages } from '../index';

/**
 * Type-safe translation hook with namespace support
 */
export function useTypedTranslation<N extends Namespace>(
  ns?: N | N[],
  options?: UseTranslationOptions<N>
) {
  return useTranslation(ns, options);
}

/**
 * Hook for common namespace translations
 * Use for: buttons, status labels, pagination, time formatting
 */
export function useCommonTranslation() {
  return useTranslation('common');
}

/**
 * Hook for auth namespace translations
 * Use for: login, register, password reset, logout
 */
export function useAuthTranslation() {
  return useTranslation('auth');
}

/**
 * Hook for survey namespace translations
 * Use for: public survey taking, question types, validation
 */
export function useSurveyTranslation() {
  return useTranslation('survey');
}

/**
 * Hook for builder namespace translations
 * Use for: survey builder UI, question editors, options management
 */
export function useBuilderTranslation() {
  return useTranslation('builder');
}

/**
 * Hook for dashboard namespace translations
 * Use for: survey list, cards, filters, stats, sharing
 */
export function useDashboardTranslation() {
  return useTranslation('dashboard');
}

// Quiz and Assessment modules removed - hooks disabled
// /**
//  * Hook for quiz namespace translations
//  * Use for: quiz list, builder, taking, results
//  */
// export function useQuizTranslation() {
//   return useTranslation('quiz');
// }

// /**
//  * Hook for assessment namespace translations
//  * Use for: assessment list, builder, rubrics, submissions, peer review
//  */
// export function useAssessmentTranslation() {
//   return useTranslation('assessment');
// }

/**
 * Hook for validation namespace translations
 * Use for: form validation messages, field errors
 */
export function useValidationTranslation() {
  return useTranslation('validation');
}

/**
 * Hook for error namespace translations
 * Use for: error pages, API errors, network errors
 */
export function useErrorTranslation() {
  return useTranslation('error');
}

/**
 * Hook for landing page translations
 * Use for: public landing page, hero section, features overview
 */
export function useLandingTranslation() {
  return useTranslation('landing');
}

/**
 * Hook for features page translations
 * Use for: features page, feature descriptions, capabilities
 */
export function useFeaturesTranslation() {
  return useTranslation('features');
}

/**
 * Hook for about page translations
 * Use for: about page, company info, mission
 */
export function useAboutTranslation() {
  return useTranslation('about');
}

/**
 * Hook for FAQ page translations
 * Use for: FAQ page, questions and answers
 */
export function useFaqTranslation() {
  return useTranslation('faq');
}

/**
 * Hook for billing namespace translations
 * Use for: subscription plans, credits, invoices, payments
 */
export function useBillingTranslation() {
  return useTranslation('billing');
}

/**
 * Hook for language switching functionality
 */
export function useLanguage() {
  const { i18n: i18nInstance } = useTranslation();

  const currentLanguage = i18nInstance.language as SupportedLanguage;

  const changeLanguage = useCallback(
    async (lang: SupportedLanguage) => {
      await i18nInstance.changeLanguage(lang);
    },
    [i18nInstance]
  );

  const getCurrentLanguageInfo = useCallback(() => {
    return supportedLanguages.find((lang) => lang.code === currentLanguage);
  }, [currentLanguage]);

  return {
    currentLanguage,
    changeLanguage,
    supportedLanguages,
    getCurrentLanguageInfo,
    isRTL: false, // Add RTL support when needed
  };
}

/**
 * Hook for multiple namespaces at once
 * Useful when a component needs translations from several namespaces
 */
export function useMultipleTranslations<N extends Namespace>(namespaces: N[]) {
  return useTranslation(namespaces);
}

/**
 * Helper function to get validation message with parameters
 * @param t - Translation function from useValidationTranslation
 * @param key - Validation key (e.g., 'email.required', 'password.minLength')
 * @param params - Interpolation parameters
 */
export function getValidationMessage(
  t: ReturnType<typeof useValidationTranslation>['t'],
  key: string,
  params?: Record<string, string | number>
): string {
  return t(key, params);
}

/**
 * Helper function to format date according to current locale
 */
export function useLocalizedDateFormat() {
  const { i18n: i18nInstance, t: tCommon } = useTranslation('common');

  const formatDate = useCallback(
    (date: Date | string, options?: Intl.DateTimeFormatOptions) => {
      const dateObj = typeof date === 'string' ? new Date(date) : date;
      const locale = i18nInstance.language === 'sq' ? 'sq-AL' : 'en-US';

      return new Intl.DateTimeFormat(locale, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        ...options,
      }).format(dateObj);
    },
    [i18nInstance.language]
  );

  const formatRelativeTime = useCallback(
    (date: Date | string) => {
      const dateObj = typeof date === 'string' ? new Date(date) : date;
      const now = new Date();
      const diffMs = now.getTime() - dateObj.getTime();
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMins < 1) return tCommon('time.now');
      if (diffMins < 60) return tCommon('time.minutesAgo', { count: diffMins });
      if (diffHours < 24) return tCommon('time.hoursAgo', { count: diffHours });
      if (diffDays < 7) return tCommon('time.daysAgo', { count: diffDays });

      return formatDate(dateObj);
    },
    [formatDate, tCommon]
  );

  return { formatDate, formatRelativeTime };
}

// Re-export i18n instance for direct access when needed
export { i18n };
